import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { EmployeeCalendarRegisterStatus } from 'src/app/enums/employee-calendar-register-status.enum';
import { ProductionKeyPerformanceIndicatorEvaluation } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { SprintReviewExecutionData, SprintReviewExecutionItem } from 'src/app/models/sprint-review-execution-kpi-data.model';

interface SprintReviewRow {
  date: string;
  participants_count: number | null;
  status: EmployeeCalendarRegisterStatus;
}

@Component({
  selector: 'app-sprint-review-execution-details',
  imports: [CommonModule, DatePipe, MatIconModule],
  templateUrl: './sprint-review-execution-details.component.html',
  styleUrl: './sprint-review-execution-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SprintReviewExecutionDetailsComponent {
  currentMonthEvaluation = input<ProductionKeyPerformanceIndicatorEvaluation | null>(null);
  readonly EmployeeCalendarRegisterStatus = EmployeeCalendarRegisterStatus;

  detailsData = computed((): SprintReviewExecutionData | null => {
    const rawData = this.currentMonthEvaluation()?.data;
    if (SprintReviewExecutionDetailsComponent.isSprintReviewExecutionData(rawData)) {
      return rawData;
    }

    if (typeof rawData !== 'object' || rawData === null) {
      return null;
    }

    const nestedData = (rawData as { company_product_general_kpi?: { kpi_data?: unknown } }).company_product_general_kpi?.kpi_data;
    return SprintReviewExecutionDetailsComponent.isSprintReviewExecutionData(nestedData) ? nestedData : null;
  });

  completedReviewsCount = computed((): number => this.detailsData()?.completed_reviews_count ?? 0);
  upcomingReviewsCount = computed((): number => this.detailsData()?.upcoming_reviews_count ?? 0);
  totalReviewsCount = computed((): number => this.completedReviewsCount() + this.upcomingReviewsCount());

  sprintReviews = computed((): SprintReviewRow[] => {
    const data = this.detailsData();
    if (!data) {
      return [];
    }

    return data.sprint_reviews_data
      .map(
        (item: SprintReviewExecutionItem): SprintReviewRow => ({
          date: item.date,
          participants_count: item.participants_count,
          status: item.status,
        })
      )
      .sort((a, b) => a.date.localeCompare(b.date));
  });

  public static isSprintReviewExecutionData(data: unknown): data is SprintReviewExecutionData {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const candidate = data as Partial<SprintReviewExecutionData>;

    return (
      typeof candidate.completed_reviews_count === 'number' &&
      typeof candidate.upcoming_reviews_count === 'number' &&
      Array.isArray(candidate.sprint_reviews_data)
    );
  }
}
